import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BackgroundTexture from "@/assets/svg/backgroudTextureSvg";
import Button from "@/components/Button/Button";
import Loader from "@/components/Loader/Loader";
import { openRazorpayCheckout } from "@/lib/razorPay/razorpayCheckout";
import { combineClasses, getMySubscription, saveMySubscription } from "@/lib/utils";
import RazorpayService from "@/services/api/razorpay";
import SubscriptionService from "@/services/api/subscription";
import { PROFILE_BASE_PATH, PROFILE_CHILD_PATHS } from "@/pages/Profile/profilePaths";
import routes from "@/routes/routes";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchSubscriptionPlans } from "@/store/slices/subscriptionSlice";
import styles from "./Membership.module.scss";

const formatPlanAmount = (amount: number) =>
  `₹ ${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const formatPlanDuration = (months: number) =>
  months === 1 ? "Per Month" : `${months} Month`;

const Membership = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const subscription = useMemo(() => getMySubscription(), []);
  const { plans, isLoadingPlans, plansError } = useAppSelector((state) => state.subscription);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchSubscriptionPlans());
  }, [dispatch]);

  useEffect(() => {
    if (plans.length === 0 || hasInitializedSelection) {
      return;
    }

    const storedSubscription = getMySubscription();
    const hasActiveSubscriptionOnLoad = storedSubscription?.status === "ACTIVE";

    setSelectedPlanId(hasActiveSubscriptionOnLoad ? null : plans[0]?.id ?? null);
    setHasInitializedSelection(true);
  }, [hasInitializedSelection, plans]);

  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId) ?? null;
  const error = plansError || paymentError;
  const isLoading = isLoadingPlans && plans.length === 0;

  const hasActiveSubscription = subscription?.status === "ACTIVE";
  const queuedPlanId =
    subscription?.planQueued && subscription?.queuedSubscription
      ? subscription.queuedSubscription.planId
      : null;
  const isQueuedPlanSelected = Boolean(
    hasActiveSubscription && queuedPlanId && selectedPlanId === queuedPlanId,
  );
  const isCurrentPlanSelected = Boolean(
    hasActiveSubscription && subscription?.planId && selectedPlanId === subscription.planId,
  );
  const isContinueDisabled =
    !selectedPlan || isPaying || isQueuedPlanSelected || isCurrentPlanSelected;

  const handleContinue = async () => {
    if (!selectedPlan || isContinueDisabled) {
      return;
    }

    if (hasActiveSubscription) {
      navigate(
        `${PROFILE_BASE_PATH}/${PROFILE_CHILD_PATHS.CHANGE_MEMBERSHIP.replace(":planId", String(selectedPlan.id))}`,
      );
      return;
    }

    try {
      setIsPaying(true);
      setPaymentError(null);

      const response = await RazorpayService.orderInitiate({
        planId: selectedPlan?.id,
        paymentProvider: "RAZORPAY_SUBSCRIPTION",
      });

      const { paymentOrderId, paymentKeyId } = response?.data || {};

      if (!paymentOrderId || !paymentKeyId) {
        throw new Error("Unable to start payment");
      }

      await openRazorpayCheckout({
        key: paymentKeyId,
        subscription_id: paymentOrderId,
        onSuccess: async (response) => {
          try {
            await RazorpayService.subscriptionCallback(response);

            const subscriptionResponse =
              await SubscriptionService.getUserSubscription();

            if (subscriptionResponse?.data) {
              saveMySubscription(subscriptionResponse?.data);
            }

            navigate(routes.privateRoutes.PAYMENT_SUCCESSFUL.path);
          } catch (callbackError) {
            console.error(callbackError);
            setPaymentError("Payment verification failed. Please contact support.");
          }
        },
        onFailure: () => {
          setPaymentError("Payment failed. Please try again.");
        },
      });
    } catch (err) {
      console.error(err);
      setPaymentError("Unable to start payment. Please try again.");
    } finally {
      setIsPaying(false);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <section aria-labelledby="membership-title" className={styles.page}>
      <BackgroundTexture
        aria-hidden="true"
        className={styles.backgroundTextureTop}
      />
      <BackgroundTexture
        aria-hidden="true"
        className={styles.backgroundTextureBottom}
      />

      <div className={styles.content}>
        <div className={styles.main}>
          <img alt="BFC" className={styles.logo} src="/BFClogo.png" />

          <div className={styles.header}>
            <h1 className={styles.title} id="membership-title">
              Choose Your <span className={styles.titleAccent}>BFC</span> Membership
            </h1>
            <p className={styles.subtitle}>
              A better way for founders to connect.
            </p>
          </div>

          {error ? <p className={styles.error}>{error}</p> : null}

          <div className={styles.planList} role="radiogroup" aria-label="Membership plans">
            {plans?.map((plan) => {
              const isActive = plan.id === selectedPlanId;
              const isQueuedPlan = queuedPlanId === plan.id;
              const tag = plan?.tag?.trim();

              return (
                <button
                  key={plan.id}
                  aria-checked={isActive}
                  className={combineClasses(
                    styles.planCard,
                    isActive ? styles.planCardActive : "",
                  )}
                  onClick={() => setSelectedPlanId(plan.id)}
                  role="radio"
                  type="button"
                >
                  {tag ? <span className={styles.planTag}>{tag}</span> : null}

                  <div className={styles.planPriceRow}>
                    <span className={styles.planAmount}>
                      {formatPlanAmount(plan?.amount)}
                    </span>
                    <span className={styles.planDuration}>
                      / {formatPlanDuration(plan?.planDurationMonth)}
                    </span>
                  </div>

                  {plan?.description ? (
                    <p className={styles.planDescription}>{plan?.description}</p>
                  ) : null}

                  {isQueuedPlan ? (
                    <span className={styles.queuedBadge}>
                      <span className={styles.queuedDot} />
                      This plan is queued
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <Button
          className={styles.continueButton}
          disabled={isContinueDisabled}
          onClick={handleContinue}
          type="button"
        >
          {isPaying ? "Processing..." : "Continue"}
        </Button>
      </div>
    </section>
  );
};

export default Membership;
